import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const lastUpdated = '11 May 2026';

const sections = [
  {
    id: 'introduction',
    title: '1. Introduction',
    body: (
      <>
        <p>
          Thermal Systems Ltd (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) operates the TS1 Dashboard (the
          &quot;Service&quot;). This Privacy Policy explains how we collect, use, disclose, and safeguard your
          information when you use the Service. Please read it carefully.
        </p>
        <p className="mt-3">
          By using the Service, you agree to the practices described here. If you do not agree, please do not use the
          Service.
        </p>
      </>
    ),
  },
  {
    id: 'controller',
    title: '2. Data controller',
    body: (
      <p>
        The data controller responsible for your personal data in relation to the Service is{' '}
        <strong>Thermal Systems Ltd</strong>. You can contact us by telephone at{' '}
        <a href="tel:02034881030" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          020 3488 1030
        </a>
        , via our website{' '}
        <a
          href="https://thermal-systems.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          thermal-systems.co.uk
        </a>
        , or through the contact channels your organisation has been given for support.
      </p>
    ),
  },
  {
    id: 'collect',
    title: '3. Information we may collect',
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Account and identity data:</strong> such as company name, username, email address, role, and
          credentials used to authenticate you (including, where enabled, data processed for biometric or
          password-based login).
        </li>
        <li>
          <strong>Operational and technical data:</strong> details relating to sensors, sites, zones, alarms,
          temperature readings, connectivity metrics (for example signal strength), device identifiers, and similar
          telemetry needed to provide monitoring features.
        </li>
        <li>
          <strong>Usage and audit data:</strong> logs of actions taken in the Service where required for security,
          compliance, or troubleshooting, consistent with your organisation’s settings and our retention policies.
        </li>
        <li>
          <strong>Device and connection data:</strong> such as IP address, browser type, and approximate location
          inferred from network information, to secure the Service and improve reliability.
        </li>
      </ul>
    ),
  },
  {
    id: 'use',
    title: '4. How we use your information',
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>To provide, operate, and maintain the TS1 Dashboard and related features.</li>
        <li>To authenticate users and enforce access controls for your organisation.</li>
        <li>To process alarm events, notifications, reports, and analytics you or your organisation have enabled.</li>
        <li>To detect, prevent, and respond to security incidents, fraud, or misuse.</li>
        <li>To comply with legal obligations and enforce our terms.</li>
        <li>To communicate with you about the Service, support, or important changes where appropriate.</li>
      </ul>
    ),
  },
  {
    id: 'legal-basis',
    title: '5. Legal basis (UK & GDPR)',
    body: (
      <p>
        Where the UK GDPR applies, we process personal data on the basis of:{' '}
        <strong>contract</strong> (to provide the Service to your organisation),{' '}
        <strong>legitimate interests</strong> (for example maintaining security and improving the Service, balanced
        against your rights), <strong>legal obligation</strong> where applicable, and{' '}
        <strong>consent</strong> where we explicitly ask for it (you may withdraw consent at any time where
        processing is based solely on consent).
      </p>
    ),
  },
  {
    id: 'sharing',
    title: '6. Sharing and subprocessors',
    body: (
      <p>
        We do not sell your personal data. We may share information with service providers who assist in hosting,
        authentication, communications, or analytics, subject to appropriate contracts and safeguards. We may also
        disclose information if required by law, to protect rights, or in connection with a business transfer. Your
        organisation’s administrator may control certain integrations or data flows; refer to them for contractual
        arrangements that apply to your deployment. The categories of sub-processors we currently use are listed
        in our{' '}
        <Link to="/dpa" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          Data Processing Agreement
        </Link>
        .
      </p>
    ),
  },
  {
    id: 'retention',
    title: '7. Retention',
    body: (
      <p>
        We retain personal and operational data only as long as necessary for the purposes described in this policy,
        to meet legal or regulatory requirements, or as agreed with your organisation. Retention periods may vary by
        data category and configuration.
      </p>
    ),
  },
  {
    id: 'security',
    title: '8. Security',
    body: (
      <p>
        We implement appropriate technical and organisational measures designed to protect personal data. No method of
        transmission or storage is 100% secure; we encourage strong passwords, multi-factor or biometric options where
        offered, and prompt reporting of suspected unauthorised access.
      </p>
    ),
  },
  {
    id: 'rights',
    title: '9. Your rights',
    body: (
      <p>
        Depending on your location, you may have rights to access, rectify, erase, restrict, or object to certain
        processing of your personal data, and in some cases to data portability or to lodge a complaint with a
        supervisory authority. Many requests are handled through your organisation’s administrator. You may also
        contact us using the details in section 2. We will respond within the timeframes required by applicable law.
      </p>
    ),
  },
  {
    id: 'cookies',
    title: '10. Cookies and similar technologies',
    body: (
      <p>
        The Service uses cookies and similar technologies that are strictly necessary for authentication and
        session management, plus locally stored preferences such as theme. See our{' '}
        <Link to="/cookies" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          Cookie Policy
        </Link>{' '}
        for details.
      </p>
    ),
  },
  {
    id: 'processor-role',
    title: '11. Our role as processor for customer organisations',
    body: (
      <p>
        Where the Service is provided to a customer organisation (for example, your employer or a
        distributor&apos;s end customer), that organisation is the data controller of personal data
        processed in their tenant, and Thermal Systems Ltd acts as data processor on their behalf in
        relation to that data. The terms on which we do so are set out in our{' '}
        <Link to="/dpa" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          Data Processing Agreement
        </Link>
        . Requests to exercise data-subject rights in respect of that data should generally be directed to
        your organisation in the first instance.
      </p>
    ),
  },
  {
    id: 'children',
    title: '12. Children',
    body: (
      <p>
        The Service is intended for business use and is not directed at children. We do not knowingly collect personal
        data from children.
      </p>
    ),
  },
  {
    id: 'transfers',
    title: '13. International transfers',
    body: (
      <p>
        If personal data is processed outside the UK or EEA, we will ensure appropriate safeguards (such as standard
        contractual clauses or adequacy decisions) are in place where required by law.
      </p>
    ),
  },
  {
    id: 'changes',
    title: '14. Changes to this policy',
    body: (
      <p>
        We may update this Privacy Policy from time to time. We will post the revised version on this page and update
        the &quot;Last updated&quot; date. Material changes may be communicated through the Service or by other
        appropriate means.
      </p>
    ),
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <Link
          to="/login"
          className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to sign in
        </Link>

        <header className="mb-10 pb-8 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Last updated: {lastUpdated}</p>
          <p className="mt-4 text-gray-600 dark:text-gray-300">
            This policy describes how Thermal Systems Ltd handles information in connection with the TS1 Dashboard.
          </p>
        </header>

        <nav className="mb-10 p-4 rounded-lg bg-app-panel dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Contents</h2>
          <ul className="text-sm space-y-1 text-indigo-600 dark:text-indigo-400">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="hover:underline">
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-10 text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
          {sections.map((s) => (
            <section key={s.id} id={s.id}>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{s.title}</h2>
              {s.body}
            </section>
          ))}
        </div>

        <footer className="mt-14 pt-8 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-3">
            <Link to="/terms" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Terms of Service
            </Link>
            <Link to="/compliance" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Security &amp; Compliance
            </Link>
            <Link to="/cookies" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Cookie Policy
            </Link>
            <Link to="/dpa" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Data Processing Agreement
            </Link>
          </div>
          <p>© {new Date().getFullYear()} Thermal Systems Ltd. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
